import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import mountPage from './page-lifecycle';
import {snapshotPage, revealPage} from './content-motion';
import {currentLocale, translateContent} from './preferences';
import {executable} from './article-runtime';
import preservePortalHistory from './portal-notification';

const mainSelector = '#main-content[data-soft-navigation="1"]';
const scrollRegions = '[data-bookmark-viewport], .bookmark-viewport, [data-activity-viewport]';
const eligible = value => {
    const url = new URL(value, location.href);
    return url.origin === location.origin && /^https?:$/.test(url.protocol) &&
        !/^\/(ghost|members|public|assets|content|cdn-cgi|rss|signin|signup|account)(\/|$)/.test(url.pathname) &&
        !/\.[a-z\d]{2,8}$/i.test(url.pathname) && !url.searchParams.has('token') && !url.hash.startsWith('#/portal');
};

export default function navigation(menu) {
    let main = document.querySelector(mainSelector);
    if (!main) return;
    let page = mountPage(main, {initial: true});
    let hardNavigation = false;
    const positions = new Map();
    const singleton = script => script.matches('script[src*="/assets/js/theme-init.js"], script[src*="/assets/built/index.js"], script[src*="/assets/built/shell.js"], script[data-ghost], script[data-sodo-search], script[data-ghost-comments-counts-api], script[src*="/public/cards.min.js"], script[src*="/public/member-attribution.min.js"]');
    const themeAssets = doc => [
        doc.querySelector('script[src*="/built/index.js"]')?.getAttribute('src'),
        doc.querySelector('script[src*="/built/shell.js"]')?.getAttribute('src'),
        doc.querySelector('link[href*="/built/index.css"]')?.getAttribute('href')
    ].join('|');
    const bundle = themeAssets(document);

    const swup = new Swup({
        containers: ['#main-content'], native: true, animationSelector: false,
        animateHistoryBrowsing: true, cache: false, timeout: 8000,
        ignoreVisit: (value, {el} = {}) => {
            const url = new URL(value, location.href);
            return !eligible(url) || !!el?.closest('[data-no-swup], [data-portal]') ||
                !!(url.hash && url.pathname === location.pathname && url.search === location.search);
        },
        plugins: [new SwupHeadPlugin({
            awaitAssets: true,
            // Keep the running theme/Portal/search and their runtime styles. Page
            // metadata and page-only assets still follow the incoming document.
            persistTags: tag => singleton(tag) || tag.matches('#gh-members-styles, #tiwb-search-glass, link[href*="/assets/built/index.css"]')
        }), new SwupBodyClassPlugin()]
    });
    preservePortalHistory();
    let renderedIndex = history.state?.index;
    const capture = () => ({
        x: scrollX, y: scrollY, state: page.snapshot(),
        regions: [...main.querySelectorAll(scrollRegions)].map(node => [node.scrollLeft, node.scrollTop])
    });
    const restore = position => {
        main.querySelectorAll(scrollRegions).forEach((node, index) => {
            const offset = position.regions[index];
            if (offset) { node.scrollLeft = offset[0]; node.scrollTop = offset[1]; }
        });
        window.scrollTo({left: position.x, top: position.y, behavior: 'instant'});
    };

    swup.hooks.on('visit:start', visit => {
        menu?.close();
        document.querySelector('#sodo-search-root iframe')?.contentDocument?.dispatchEvent(new KeyboardEvent('keyup', {key: 'Escape', bubbles: true}));
        positions.set(renderedIndex, capture());
        visit.meta.saved = visit.history.popstate ? positions.get(history.state?.index) : null;
        visit.meta.controller = new AbortController();
        visit.animation.wait = true;
        document.documentElement.dataset.navigating = 'true';
    });
    swup.hooks.replace('fetch:request', async (visit, {url, options}) => {
        const controller = visit.meta.controller;
        const abort = () => controller.abort();
        options.signal?.addEventListener('abort', abort, {once: true});
        if (options.signal?.aborted) abort();
        try {
            const response = await fetch(url, {...options, signal: controller.signal});
            if (!response.ok || !eligible(response.url) || !response.headers.get('content-type')?.includes('text/html')) {
                throw new Error('This destination requires a regular navigation');
            }
            return response;
        } finally { options.signal?.removeEventListener('abort', abort); }
    });
    swup.hooks.replace('page:load', async (visit, args, load) => {
        try {
            const incoming = await load(visit, args);
            const doc = new DOMParser().parseFromString(incoming.html, 'text/html');
            const next = doc.querySelector(mainSelector);
            if (!next || themeAssets(doc) !== bundle) {
                throw new Error('The destination uses a different theme document');
            }
            // Decode a visible cover while the old page stays live; never wait
            // for third-party widgets inside the View Transition update callback.
            const cover = next.querySelector('img[loading="eager"], .gh-feature-image img, .hero img');
            if (cover) {
                const picture = (cover.closest('picture') || cover).cloneNode(true);
                const image = picture.tagName === 'IMG' ? picture : picture.querySelector('img');
                image.loading = 'eager';
                picture.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;max-width:100vw';
                document.body.append(picture);
                try { await Promise.race([image.decode().catch(() => {}), new Promise(resolve => setTimeout(resolve, 180))]); }
                finally { picture.remove(); }
            }
            return incoming;
        } catch (error) {
            // Swup also attaches a success-only observer to this promise. Resolve
            // expected failures so aborted/unsupported visits cannot leak an
            // unhandled rejection; its ignored-visit path performs the fallback.
            if (error?.aborted) visit.abort();
            else if (!visit.done) { hardNavigation = true; visit.ignore(); delete document.documentElement.dataset.navigating; }
            return {url: visit.to.url, html: ''};
        }
    });
    swup.hooks.before('content:replace', visit => {
        const doc = visit.to.document;
        doc.documentElement.lang = currentLocale();
        translateContent(doc.querySelector(mainSelector));
        // Only page-owned injections are replayed. Never replay the theme,
        // Ghost integrations already running in the shell. Custom injections may
        // use tiwb:page-ready / tiwb:page-leave to own their route lifecycle.
        visit.meta.scripts = [...doc.querySelectorAll('script')].filter(script => {
            if (!executable(script) || script.closest('#main-content')) return false;
            if (singleton(script)) return false;
            script.remove();
            return true;
        });
        positions.set(renderedIndex, capture());
        visit.meta.outgoingTop = main.getBoundingClientRect().top;
        if (!visit.animation.native || !document.startViewTransition) {
            visit.meta.snapshot = snapshotPage();
            visit.meta.runtimeReady = new Promise(resolve => { visit.meta.resumeRuntime = resolve; });
        }
        page.destroy();
    }, {priority: -100});
    swup.hooks.on('content:replace', async visit => {
        main = document.querySelector(mainSelector);
        if (visit.meta.snapshot) main.classList.add('page-motion-enter');
        page = mountPage(main, {state: visit.meta.saved?.state, extraScripts: visit.meta.scripts, runtimeReady: visit.meta.runtimeReady});
        menu?.refresh();
        renderedIndex = history.state?.index;
        // Cached widgets resolve in microtasks before the incoming snapshot.
        // Cold APIs continue normally after the page is visible.
        await Promise.resolve();
    });
    swup.hooks.before('scroll:top', (_visit, {options}) => { options.behavior = 'instant'; });
    swup.hooks.before('scroll:anchor', (_visit, {options}) => { options.behavior = 'instant'; });
    swup.hooks.replace('content:scroll', visit => {
        if (visit.meta.saved) { restore(visit.meta.saved); return true; }
        const scrolled = swup.scrollToContent(visit);
        const anchor = visit.to.hash && swup.getAnchorElement(visit.to.hash);
        (anchor || main).focus({preventScroll: true});
        return scrolled;
    });
    swup.hooks.on('page:view', visit => {
        document.documentElement.style.setProperty('--page-outgoing-y', `${visit.meta.outgoingTop - main.getBoundingClientRect().top}px`);
    });
    const finish = () => {
        delete document.documentElement.dataset.navigating;
        document.documentElement.style.removeProperty('--page-outgoing-y');
    };
    swup.hooks.on('animation:in:await', async visit => {
        if (visit.meta.snapshot) {
            await revealPage(visit.meta.snapshot, main);
            visit.meta.resumeRuntime?.();
        }
    });
    swup.hooks.on('visit:end', finish);
    swup.hooks.on('visit:abort', visit => { visit.meta.controller?.abort(); visit.meta.snapshot?.remove(); finish(); });
    swup.hooks.before('visit:fail', visit => { hardNavigation = true; visit.meta.snapshot?.remove(); finish(); });
    // Sodo results assign location.href instead of clicking anchors. The browser
    // navigation event lets those same-site assignments use the same controller.
    // Ignore Swup's own pushState/replaceState and every intentional hard fallback.
    window.navigation?.addEventListener('navigate', event => {
        if (hardNavigation || event.destination.sameDocument || !event.canIntercept || !event.cancelable ||
            !['push', 'replace'].includes(event.navigationType) || event.formData || event.downloadRequest !== null ||
            !eligible(event.destination.url)) return;
        event.preventDefault();
        swup.navigate(event.destination.url, {history: event.navigationType});
    });
}
